import { usePlayAudio } from '@shared/hooks/use-play-audio'
import { useMemo, useState } from 'react'
import { ProviderTypes } from '../../config/agent-settings.validation.config'
import { useGetLanguages, useGetProviders } from '../../knowledge-base/queries'
import { useTextToSpeechMutation } from '../../queries'
import type { PromptItem, VoicePreviewParams } from '../../types'
import { createVoicePreviewHandler } from '../../utils'
import {
  filterVoicesByLanguage,
  getDefaultVoiceForLanguage,
  SUPPORTED_LANGUAGES,
} from '../../utils/lang-utils'
import { BASIC_LANGUAGES } from './constants'

export const useAIVoices = (showAIVoices: boolean, value: PromptItem) => {
  const { data: languagesData } = useGetLanguages((data) => ({
    ...data,
    data: {
      ...data.data,
      languages: data.data.languages.filter((lang) => SUPPORTED_LANGUAGES.includes(lang.code)),
    },
  }))
  const { data: providers } = useGetProviders()
  const { isPlaying, play, stop } = usePlayAudio()
  const textToSpeechMutation = useTextToSpeechMutation()
  const [loadingVoiceId, setLoadingVoiceId] = useState<string | null>(null)

  const elevenLabsProvider = useMemo(
    () =>
      showAIVoices
        ? providers?.data?.tts_providers?.find((p) => p.id === ProviderTypes.ElevenLabs)
        : null,
    [providers, showAIVoices],
  )

  const handlePlayPreview = useMemo(
    () => async (params: VoicePreviewParams) => {
      try {
        setLoadingVoiceId(params.voiceId)
        await createVoicePreviewHandler(play, textToSpeechMutation)(params)
      } finally {
        setLoadingVoiceId(null)
      }
    },
    [play, textToSpeechMutation],
  )

  const audioHandlers = useMemo(
    () => ({ isPlaying, handlePlayPreview, loadingVoiceId, stop }),
    [isPlaying, handlePlayPreview, loadingVoiceId, stop],
  )

  const apiLanguageOptions = useMemo(() => {
    if (!showAIVoices || !languagesData?.data?.languages || !elevenLabsProvider) return []

    // Only show languages that have available voices
    return languagesData.data.languages
      .filter((lang) => {
        const voices = filterVoicesByLanguage(elevenLabsProvider.voices || [], lang.code)
        return voices.length > 0
      })
      .map((lang) => ({
        label: lang.name,
        value: lang.code,
        selectedLabel: lang.code,
      }))
  }, [languagesData, elevenLabsProvider, showAIVoices])

  const voiceOptions = useMemo(() => {
    if (value.prompt_speak_type !== ProviderTypes.ElevenLabs || !elevenLabsProvider) {
      return []
    }

    const filteredVoices = value.language
      ? filterVoicesByLanguage(elevenLabsProvider.voices || [], value.language)
      : elevenLabsProvider.voices || []

    return filteredVoices.map((voice) => ({
      label: voice.label,
      value: voice.id,
      selectedLabel: voice.label
        .split(' ')
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join(''),
      voice, // Include the full voice object for rendering
      audioHandlers, // Include audio handlers for rendering
      sttTtsConfig: {
        ttsModel: elevenLabsProvider.models?.[0]?.id || '',
        ttsProvider: ProviderTypes.ElevenLabs,
      },
    }))
  }, [value.prompt_speak_type, value.language, elevenLabsProvider, audioHandlers])

  const handleAIVoiceChange = (type: string, v: string, currentValue: PromptItem): PromptItem => {
    const updatedValue: PromptItem = {
      ...currentValue,
      [type]: v,
      file: '',
      prompt_type: 'speak',
      url: '',
    }

    if (type === 'prompt_speak_type') {
      // Set default voice based on speech processor type
      if (v === ProviderTypes.ElevenLabs && elevenLabsProvider) {
        // Check if current language exists in AI Voices list
        const apiLanguages = languagesData?.data?.languages.map((lang) => lang.code) || []
        const languageToUse =
          currentValue.language && apiLanguages.includes(currentValue.language)
            ? currentValue.language
            : 'en-US'

        updatedValue.language = languageToUse
        const defaultVoice = getDefaultVoiceForLanguage(
          elevenLabsProvider.voices || [],
          languageToUse,
        )
        updatedValue.voice = defaultVoice?.id || ''
      } else if (v === 'basic' || v === 'polly') {
        updatedValue.voice = 'WOMAN'
        // Preserve language if it exists in Basic's static list, otherwise default to en-US
        if (
          !currentValue.language ||
          !BASIC_LANGUAGES.some((lang) => lang.value === currentValue.language)
        ) {
          updatedValue.language = 'en-US'
        }
      }
    }

    // Update voice when language changes for ElevenLabs
    if (
      type === 'language' &&
      currentValue.prompt_speak_type === ProviderTypes.ElevenLabs &&
      elevenLabsProvider
    ) {
      const filteredVoices = filterVoicesByLanguage(elevenLabsProvider.voices || [], v)
      if (filteredVoices.length === 0) return currentValue
      const defaultVoice = getDefaultVoiceForLanguage(elevenLabsProvider.voices || [], v)
      updatedValue.voice = defaultVoice?.id || ''
    }

    return updatedValue
  }

  return {
    elevenLabsProvider,
    apiLanguageOptions,
    voiceOptions,
    handleAIVoiceChange,
    audioHandlers,
  }
}
